import React, { useMemo, useState } from 'react';
import {
  useShiftChangeRequests,
  useCreateShiftChangeRequest,
  useApproveShiftChangeRequest,
  useRejectShiftChangeRequest,
  useCancelShiftChangeRequest,
  useEmployees,
  useBranches,
} from '@/api';
import { Card, CardContent, Button, Input, Badge, Modal } from '@/components/ui';
import { BranchFilterSelect, LoadingSpinner, Pagination } from '@/components/common';
import { extractPaginatedData } from '@/utils/pagination';
import { Plus, Check, X, RefreshCw, ArrowRightLeft } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' | 'warning' | 'success' }> = {
  pending: { label: 'Kutilmoqda', variant: 'warning' },
  approved: { label: 'Tasdiqlangan', variant: 'success' },
  rejected: { label: 'Rad etilgan', variant: 'destructive' },
  cancelled: { label: 'Bekor qilingan', variant: 'secondary' },
};

const typeConfig: Record<string, string> = {
  swap: 'Smena almashish',
  cover: 'O‘rniga chiqish',
};

export default function ShiftChangeRequestsPage() {
  const perPage = 10;
  const { can } = useAuth();
  const canManage = can('manage_shifts');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [status, setStatus] = useState('');
  const [changeType, setChangeType] = useState('');
  const [branchId, setBranchId] = useState('');
  const [requestDate, setRequestDate] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState<Record<number, string>>({});

  const listParams = { page, per_page: perPage, status, change_type: changeType, branch_id: branchId, request_date: requestDate };
  const { data: requestsData, isLoading } = useShiftChangeRequests(listParams);
  const { data: employeesData } = useEmployees({ per_page: 200 });
  const { data: branchesData } = useBranches({ page: 1 });

  const createMutation = useCreateShiftChangeRequest();
  const approveMutation = useApproveShiftChangeRequest();
  const rejectMutation = useRejectShiftChangeRequest();
  const cancelMutation = useCancelShiftChangeRequest();

  const { items: requests, meta } = extractPaginatedData<any>(requestsData, page, perPage);
  const employees = employeesData?.data?.data || employeesData?.data || [];
  const branches = branchesData?.data?.data || branchesData?.data || [];

  const employeesByBranch = useMemo(() => {
    return employees.reduce((acc: Record<number, any[]>, employee: any) => {
      if (!employee?.branch_id) return acc;
      if (!acc[employee.branch_id]) acc[employee.branch_id] = [];
      acc[employee.branch_id].push(employee);
      return acc;
    }, {});
  }, [employees]);

  const handleApprove = async (id: number) => {
    const confirmed = window.confirm('Bu smena o‘zgarishini tasdiqlaysizmi?');
    if (!confirmed) return;
    await approveMutation.mutateAsync({ id, data: { resolution_notes: resolutionNotes[id] || undefined } });
  };

  const handleReject = async (id: number) => {
    const confirmed = window.confirm('Bu smena o‘zgarishini rad etasizmi?');
    if (!confirmed) return;
    await rejectMutation.mutateAsync({ id, data: { resolution_notes: resolutionNotes[id] || undefined } });
  };

  const handleCancel = async (id: number) => {
    const confirmed = window.confirm('Bu smena o‘zgarish so‘rovini bekor qilasizmi?');
    if (!confirmed) return;
    await cancelMutation.mutateAsync({ id, data: { resolution_notes: resolutionNotes[id] || undefined } });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <h1 className="text-3xl font-bold">Smena almashishlar</h1>
        {canManage && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Yangi so‘rov
          </Button>
        )}
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title="Smena o‘zgarish so‘rovi"
      >
        <ShiftChangeRequestForm
          branches={branches}
          employeesByBranch={employeesByBranch}
          onSubmit={async (data) => {
            await createMutation.mutateAsync(data);
            setShowForm(false);
          }}
          onCancel={() => setShowForm(false)}
          isLoading={createMutation.isPending}
        />
      </Modal>

      <Card>
        <CardContent className="py-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4 xl:grid-cols-5">
            <select
              value={changeType}
              onChange={(e) => {
                setPage(1);
                setChangeType(e.target.value);
              }}
              className="w-full border rounded-md px-3 py-2"
            >
              <option value="">Barcha turlar</option>
              <option value="swap">Smena almashish</option>
              <option value="cover">O‘rniga chiqish</option>
            </select>
            <select
              value={status}
              onChange={(e) => {
                setPage(1);
                setStatus(e.target.value);
              }}
              className="w-full border rounded-md px-3 py-2"
            >
              <option value="">Barcha statuslar</option>
              <option value="pending">Kutilmoqda</option>
              <option value="approved">Tasdiqlangan</option>
              <option value="rejected">Rad etilgan</option>
              <option value="cancelled">Bekor qilingan</option>
            </select>
            <BranchFilterSelect
              alwaysShow
              value={branchId}
              onChange={(value) => {
                setPage(1);
                setBranchId(value);
              }}
              className="w-full border rounded-md px-3 py-2"
              allLabel="Barcha filiallar"
            />
            <Input
              type="date"
              value={requestDate}
              onChange={(e) => {
                setPage(1);
                setRequestDate(e.target.value);
              }}
            />
            <Button
              variant="outline"
              onClick={() => {
                setPage(1);
                setStatus('');
                setChangeType('');
                setBranchId('');
                setRequestDate('');
              }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Tozalash
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto ui-table-wrap">
            <table className="w-full ui-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Sana</th>
                  <th className="text-left py-3 px-4 font-semibold">Tur</th>
                  <th className="text-left py-3 px-4 font-semibold">Kimdan</th>
                  <th className="text-left py-3 px-4 font-semibold">Kimga</th>
                  <th className="text-left py-3 px-4 font-semibold">Filial</th>
                  <th className="text-left py-3 px-4 font-semibold">Status</th>
                  <th className="text-left py-3 px-4 font-semibold">Izoh</th>
                  <th className="text-left py-3 px-4 font-semibold">Harakat</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-10 text-center text-gray-500">
                      Smena o‘zgarish so‘rovlari topilmadi
                    </td>
                  </tr>
                ) : (
                  requests.map((item: any) => {
                    const statusItem = statusConfig[item.status] || statusConfig.pending;
                    return (
                      <tr key={item.id} className="border-b hover:bg-gray-50 align-top">
                        <td className="py-3 px-4 whitespace-nowrap">{item.request_date}</td>
                        <td className="py-3 px-4">
                          <div className="inline-flex items-center gap-2">
                            <ArrowRightLeft className="w-4 h-4 text-gray-500" />
                            <span>{typeConfig[item.change_type] || item.change_type}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-medium">{item.requester_employee?.full_name}</div>
                          <div className="text-sm text-gray-500">{item.requester_shift?.name || '—'}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-medium">{item.target_employee?.full_name}</div>
                          <div className="text-sm text-gray-500">{item.target_shift?.name || '—'}</div>
                        </td>
                        <td className="py-3 px-4">{item.branch?.name || '—'}</td>
                        <td className="py-3 px-4">
                          <Badge variant={statusItem.variant}>{statusItem.label}</Badge>
                        </td>
                        <td className="py-3 px-4 min-w-[260px]">
                          <div className="text-sm text-gray-700 whitespace-pre-wrap">{item.reason}</div>
                          {item.status === 'pending' && canManage && (
                            <textarea
                              value={resolutionNotes[item.id] || ''}
                              onChange={(e) => setResolutionNotes((prev) => ({ ...prev, [item.id]: e.target.value }))}
                              className="mt-2 w-full rounded-md border px-3 py-2 text-sm"
                              rows={3}
                              placeholder="Manager izohi (ixtiyoriy)"
                            />
                          )}
                          {item.resolution_notes && item.status !== 'pending' && (
                            <div className="mt-2 text-sm text-gray-500 whitespace-pre-wrap">{item.resolution_notes}</div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {item.status === 'pending' && canManage ? (
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => handleApprove(item.id)} disabled={approveMutation.isPending}>
                                <Check className="w-4 h-4 mr-1" />
                                Tasdiqlash
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => handleReject(item.id)} disabled={rejectMutation.isPending}>
                                <X className="w-4 h-4 mr-1" />
                                Rad etish
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleCancel(item.id)} disabled={cancelMutation.isPending}>
                                Bekor qilish
                              </Button>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Pagination meta={meta} onPageChange={setPage} />
    </div>
  );
}

type ShiftChangeRequestFormProps = {
  branches: any[];
  employeesByBranch: Record<number, any[]>;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
};

function ShiftChangeRequestForm({ branches, employeesByBranch, onSubmit, onCancel, isLoading }: ShiftChangeRequestFormProps) {
  const [branchId, setBranchId] = useState('');
  const [changeType, setChangeType] = useState<'swap' | 'cover'>('cover');
  const [requestDate, setRequestDate] = useState('');
  const [requesterEmployeeId, setRequesterEmployeeId] = useState('');
  const [targetEmployeeId, setTargetEmployeeId] = useState('');
  const [reason, setReason] = useState('');

  const availableEmployees = branchId ? (employeesByBranch[Number(branchId)] || []) : [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({
      change_type: changeType,
      request_date: requestDate,
      requester_employee_id: Number(requesterEmployeeId),
      target_employee_id: Number(targetEmployeeId),
      reason,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium mb-1">Filial *</label>
          <select
            value={branchId}
            onChange={(e) => {
              setBranchId(e.target.value);
              setRequesterEmployeeId('');
              setTargetEmployeeId('');
            }}
            required
            className="w-full border rounded-md px-3 py-2"
          >
            <option value="">Filial tanlang</option>
            {branches.map((branch: any) => (
              <option key={branch.id} value={branch.id}>{branch.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">So‘rov turi *</label>
          <select
            value={changeType}
            onChange={(e) => setChangeType(e.target.value as 'swap' | 'cover')}
            required
            className="w-full border rounded-md px-3 py-2"
          >
            <option value="cover">O‘rniga chiqish</option>
            <option value="swap">Smena almashish</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Sana *</label>
          <Input type="date" value={requestDate} onChange={(e) => setRequestDate(e.target.value)} required />
        </div>
        <div />
        <div>
          <label className="block text-sm font-medium mb-1">Asl xodim *</label>
          <select
            value={requesterEmployeeId}
            onChange={(e) => setRequesterEmployeeId(e.target.value)}
            required
            disabled={!branchId}
            className="w-full border rounded-md px-3 py-2 disabled:bg-gray-100"
          >
            <option value="">Xodim tanlang</option>
            {availableEmployees.map((employee: any) => (
              <option key={employee.id} value={employee.id}>{employee.full_name} ({employee.employee_id})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Almashuvchi xodim *</label>
          <select
            value={targetEmployeeId}
            onChange={(e) => setTargetEmployeeId(e.target.value)}
            required
            disabled={!branchId}
            className="w-full border rounded-md px-3 py-2 disabled:bg-gray-100"
          >
            <option value="">Xodim tanlang</option>
            {availableEmployees
              .filter((employee: any) => String(employee.id) !== requesterEmployeeId)
              .map((employee: any) => (
                <option key={employee.id} value={employee.id}>{employee.full_name} ({employee.employee_id})</option>
              ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Sabab *</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
          rows={4}
          className="w-full rounded-md border px-3 py-2"
          placeholder={changeType === 'cover' ? 'Masalan: Bobur kasal bo‘ldi, Alisher uning o‘rniga chiqadi' : 'Masalan: Bobur va Alisher 20-aprel smenalarini almashtiradi'}
        />
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>Bekor qilish</Button>
        <Button type="submit" disabled={isLoading}>{isLoading ? 'Saqlanmoqda...' : 'Yuborish'}</Button>
      </div>
    </form>
  );
}
